<!--
* @FileDescription: 方案生态-右侧发布人信息区（脱敏/申请/审批；云企业详情卡片布局，与 client 对齐字段样式）
-->
<template>
  <div class="detail-wrap">
    <div class="detail-card">
      <div v-if="!enabled" class="p-empty">请选择对话后查看发布人信息。</div>

      <template v-else>
        <div v-if="loading" class="p-loading">加载中…</div>

        <div v-else class="detail-fill">
          <div class="response-unified">
            <div class="response-unified-top">
              <div class="response-main-title">响应信息</div>
              <span v-if="statusChip" class="match-badge" :class="statusChip.className">{{ statusChip.text }}</span>
            </div>

            <div class="response-unified-body">
              <div class="response-scroll">
                <div class="p-info" :class="{ blurred: shouldBlur }" aria-label="publisher-detail">
                  <div class="p-blur-tip" v-if="shouldBlur">信息公开后可查看</div>

                  <div class="blur-inner">
                    <div class="response-entity-row">
                      <div class="company-icon" aria-hidden="true">{{ cardInitial }}</div>
                      <el-tooltip
                        class="entity-name-tip"
                        :content="tooltipPlain(headerCompanyName)"
                        placement="top"
                        effect="dark"
                        popper-class="profile-panel-tip"
                        :disabled="shouldBlur || tipDisabled(headerCompanyName)"
                      >
                        <div class="company-name block-ellip-2">{{ headerCompanyName }}</div>
                      </el-tooltip>
                    </div>

                    <div class="info-bar info-bar--in-card" aria-label="summary-bar">
                      <div v-for="(it, idx) in infoBarItems" :key="'bar-' + idx" class="info-item">
                        <span class="info-label">{{ it.label }}：</span>
                        <el-tooltip
                          class="info-value-tip"
                          :content="tooltipPlain(it.value)"
                          placement="top"
                          effect="dark"
                          popper-class="profile-panel-tip"
                          :disabled="shouldBlur || tipDisabled(it.value)"
                        >
                          <span class="info-value block-ellip-1">{{ it.value }}</span>
                        </el-tooltip>
                      </div>
                    </div>

                    <div class="section-title">企业基本信息</div>
                    <div class="detail-grid">
                      <div v-for="cell in basicGridCells" :key="cell.key" class="detail-item">
                        <div class="item-label">{{ cell.label }}</div>
                        <el-tooltip
                          class="cell-value-tip"
                          :content="tooltipPlain(cell.value)"
                          placement="top"
                          effect="dark"
                          popper-class="profile-panel-tip"
                          :disabled="shouldBlur || tipDisabled(cell.value)"
                        >
                          <div class="item-value" :class="gridValueEllipClass(cell)">{{ cell.value }}</div>
                        </el-tooltip>
                      </div>
                    </div>

                    <div class="section-title">企业简介</div>
                    <div class="profile-section">
                      <el-tooltip
                        :content="tooltipPlain(introText)"
                        placement="top"
                        effect="dark"
                        popper-class="profile-panel-tip"
                        :disabled="shouldBlur || tipDisabled(introText)"
                      >
                        <div class="profile-text block-ellip-4">{{ introText }}</div>
                      </el-tooltip>
                    </div>

                    <div class="section-title">客情资源分析</div>
                    <div class="resource-section">
                      <el-tooltip
                        :content="tooltipPlain(aiInsightText)"
                        placement="top"
                        effect="dark"
                        popper-class="profile-panel-tip"
                        :disabled="shouldBlur || tipDisabled(aiInsightText)"
                      >
                        <div class="resource-text block-ellip-5">{{ aiInsightText }}</div>
                      </el-tooltip>
                    </div>

                    <template v-if="qualificationList.length">
                      <div class="section-title">资质荣誉附件</div>
                      <div class="attachment-list">
                        <div v-for="(name, qIdx) in qualificationList" :key="'q-' + qIdx" class="attach-item">
                          <span class="attach-icon">📄</span>
                          <el-tooltip
                            :content="tooltipPlain(name)"
                            placement="top"
                            effect="dark"
                            popper-class="profile-panel-tip"
                            :disabled="shouldBlur || tipDisabled(name)"
                          >
                            <span class="attach-name block-ellip-1">{{ name }}</span>
                          </el-tooltip>
                        </div>
                      </div>
                    </template>

                    <div v-if="receiveTypeKey === 'scheme' && schemeSolutionRows.length" class="scheme-solutions scheme-solutions--in-card">
                      <div class="section-title">解决方案列表</div>
                      <ul class="scheme-list">
                        <li v-for="s in schemeSolutionRows" :key="String(s.id)" class="scheme-li">
                          <el-tooltip
                            :content="tooltipPlain(safeText(s.name) + (s.onShelf === false ? '（已下架）' : ''))"
                            placement="top"
                            effect="dark"
                            popper-class="profile-panel-tip"
                            :disabled="shouldBlur || tipDisabled(safeText(s.name))"
                          >
                            <div class="scheme-name block-ellip-2">
                              {{ safeText(s.name) }}
                              <span v-if="s.onShelf === false" class="scheme-muted">（已下架）</span>
                            </div>
                          </el-tooltip>
                          <el-tooltip
                            v-if="safeText(s.solutionTag) !== '-'"
                            :content="tooltipPlain(safeText(s.solutionTag))"
                            placement="top"
                            effect="dark"
                            popper-class="profile-panel-tip"
                            :disabled="shouldBlur || tipDisabled(safeText(s.solutionTag))"
                          >
                            <div class="scheme-meta block-ellip-1">{{ safeText(s.solutionTag) }}</div>
                          </el-tooltip>
                          <el-tooltip
                            v-if="safeText(s.useCases) !== '-'"
                            :content="tooltipPlain(safeText(s.useCases))"
                            placement="top"
                            effect="dark"
                            popper-class="profile-panel-tip"
                            :disabled="shouldBlur || tipDisabled(safeText(s.useCases))"
                          >
                            <div class="scheme-desc block-ellip-2">{{ safeText(s.useCases) }}</div>
                          </el-tooltip>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <el-tooltip
                  v-if="noteText"
                  :content="tooltipPlain(noteText)"
                  placement="top"
                  effect="dark"
                  popper-class="profile-panel-tip"
                  :disabled="tipDisabled(noteText)"
                >
                  <div class="p-note block-ellip-3">{{ noteText }}</div>
                </el-tooltip>
              </div>

              <div class="p-actions">
                <el-button
                  v-if="canRequest"
                  class="btn btn-primary"
                  :loading="actionLoading"
                  @click="handleRequest"
                >
                  申请查看信息
                </el-button>

                <template v-if="canApproveOrReject">
                  <el-button class="btn btn-primary" :loading="actionLoading" @click="handleApprove">同意</el-button>
                  <el-button class="btn btn-secondary" :loading="actionLoading" @click="handleReject">拒绝</el-button>
                </template>

                <el-button v-if="canRefresh" class="btn btn-secondary" :loading="actionLoading" @click="refresh">
                  刷新
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  fetchProfileViewStatus,
  requestProfileView,
  approveProfileViewRequest,
  rejectProfileViewRequest
} from '../../../api/profileView.js'

function safeText(x, fallback = '-') {
  const t = x == null ? '' : String(x).trim()
  return t ? t : fallback
}

export default {
  name: 'PublisherProfilePanel',
  props: {
    enabled: { type: Boolean, default: false },
    chatKind: { type: String, default: '' },
    entityId: { type: String, default: '' },
    conversationId: { type: String, default: '' },
    targetUserId: { type: String, default: '' },
    currentUserId: { type: String, default: '' },
    targetDisplayName: { type: String, default: '' },
    senderUserId: { type: String, default: '' },
    receiverUserId: { type: String, default: '' },
    sendType: { type: String, default: '' },
    receiveType: { type: String, default: '' }
  },
  data() {
    return {
      loading: false,
      actionLoading: false,
      status: null,
      pollTimer: null,
      pollWindowUntil: 0,
      pollAttempts: 0
    }
  },
  computed: {
    /**
     * 右侧信息卡片查询的对象类型应是“对端”的类型：
     * - 若我是发送者：对端类型 = receiveType
     * - 若我是接收者：对端类型 = sendType
     * 这样在 scheme -> company 场景里，company 侧看到的是 scheme 信息。
     */
    targetType() {
      const sendType = String(this.sendType || '').trim().toLowerCase()
      const receiveType = String(this.receiveType || '').trim().toLowerCase()
      if (!sendType && !receiveType) return ''
      if (this.isSender) return receiveType || ''
      if (this.isReceiver) return sendType || ''
      // 兜底：无法判断视角时，沿用 receiveType（保持历史行为）
      return receiveType || sendType || ''
    },
    receiveTypeKey() {
      return String(this.targetType || '').trim().toLowerCase()
    },
    viewStatus() {
      const s = this.status?.viewStatus ? String(this.status.viewStatus).toUpperCase() : ''
      return s || 'MASKED'
    },
    requestId() {
      return this.status?.requestId ? String(this.status.requestId) : ''
    },
    ownerUserId() {
      return this.status?.ownerUserId ? String(this.status.ownerUserId) : ''
    },
    statusChip() {
      const s = this.viewStatus
      if (s === 'APPROVED') return { text: '已授权', className: 'ok' }
      if (s === 'PENDING') return { text: '待同意', className: 'wait' }
      if (s === 'NEED_ACTION') return { text: '待处理', className: 'warn' }
      return { text: '已脱敏', className: 'muted' }
    },
    canRequest() {
      if (!this.enabled) return false
      if (this.isDirectVisibleByRole) return false
      return this.viewStatus === 'MASKED'
    },
    canApproveOrReject() {
      if (!this.enabled) return false
      const isOwner = this.currentUserId && this.ownerUserId && this.currentUserId === this.ownerUserId
      return isOwner && this.viewStatus === 'NEED_ACTION' && !!this.requestId
    },
    canRefresh() {
      return this.enabled
    },
    isSender() {
      return !!this.currentUserId && !!this.senderUserId && this.currentUserId === this.senderUserId
    },
    isReceiver() {
      return !!this.currentUserId && !!this.receiverUserId && this.currentUserId === this.receiverUserId
    },
    isDirectVisibleByRole() {
      const sendType = String(this.sendType || '').trim().toLowerCase()
      const receiveType = String(this.receiveType || '').trim().toLowerCase()
      if (!sendType || !receiveType) return false
      const sendIsSs = sendType === 'scheme' || sendType === 'supplier'
      const receiveIsSs = receiveType === 'scheme' || receiveType === 'supplier'

      // 3) scheme/supplier <-> scheme/supplier：双方直显
      if (sendIsSs && receiveIsSs) return true
      // 1) company -> scheme/supplier：发送者直显
      if (sendType === 'company' && receiveIsSs) return this.isSender
      // 2) scheme/supplier -> company：接收者直显
      if (sendIsSs && receiveType === 'company') return this.isReceiver
      return false
    },
    shouldBlur() {
      if (this.isDirectVisibleByRole) return false
      return this.viewStatus !== 'APPROVED'
    },
    noteText() {
      const s = this.viewStatus
      if (s === 'MASKED') return '公开信息后可查看完整联系方式；你可以向对方申请查看。'
      if (s === 'PENDING') return '当前状态：待对方同意。对方同意后，你将仅在本次申请范围内查看完整信息。'
      if (s === 'NEED_ACTION') return '对方申请查看你的信息；同意后将仅对该申请人开放。'
      if (s === 'APPROVED') return '已获得授权：完整信息仅对当前申请人可见。'
      return ''
    },
    profileSource() {
      const s = this.status || {}
      const masked = s.maskedProfile || {}
      const full = s.fullProfile || {}
      // 仅用毛玻璃做权限控制，不在字段层做二次脱敏：
      // 只要后端返回 fullProfile，就优先展示 fullProfile（未授权时仍会被 shouldBlur 模糊遮罩）。
      const hasFull = full && typeof full === 'object' && Object.keys(full).length
      return hasFull ? full : masked
    },
    headerCompanyName() {
      const n = safeText(this.profileSource?.companyName, '')
      return n || safeText(this.targetDisplayName, '未命名主体')
    },
    cardInitial() {
      const n = this.headerCompanyName || ''
      const ch = n.trim().charAt(0)
      return ch || '企'
    },
    infoBarItems() {
      const src = this.profileSource || {}
      const k = this.receiveTypeKey
      if (k === 'company') {
        return [
          { label: '行业', value: safeText(src.industry) },
          { label: '注册资本', value: safeText(src.registerCapital) },
          { label: '手机号', value: safeText(src.mobilePhone) },
          { label: '办公地址', value: safeText(src.officeAddressDetail || src.address) }
        ]
      }
      if (k === 'scheme') {
        return [
          { label: '行业', value: safeText(src.industry) },
          { label: '年销售额', value: safeText(src.annualSalesRange) },
          { label: '手机号', value: safeText(src.mobilePhone) },
          { label: '办公地址', value: safeText(src.officeAddress || src.serviceArea || src.address) }
        ]
      }
      if (k === 'supplier') {
        return [
          { label: '行业', value: safeText(src.industry) },
          { label: '注册资本', value: safeText(src.registerCapital) },
          { label: '手机号', value: safeText(src.mobilePhone) },
          { label: '地址', value: safeText(src.officeAddressDetail || src.address) }
        ]
      }
      return [
        { label: '行业', value: safeText(src.industry) },
        { label: '联系方式', value: safeText(src.mobilePhone) },
        { label: '名称', value: safeText(src.companyName) },
        { label: '其他', value: safeText(src.remark) }
      ]
    },
    basicGridCells() {
      const src = this.profileSource || {}
      const k = this.receiveTypeKey
      const rows = []
      const push = (key, label, val) => {
        rows.push({ key, label, value: safeText(val) })
      }
      if (k === 'company') {
        push('legalPerson', '法人 / 负责人', firstNonDash(src.legalPerson, src.enterpriseLeader))
        push('registerYear', '成立年份', src.registerYear)
        push('annualOutputValue', '年产值', src.annualOutputValue)
        push('companyScale', '企业规模', src.companyScale)
        push('registerCapital', '注册资本', src.registerCapital)
        push('advantageAreas', '核心优势', src.advantageAreas)
        return rows
      }
      if (k === 'scheme') {
        push('legalPerson', '法人姓名', src.legalPerson)
        push('registerYear', '成立年份', src.registerYear)
        push('annualSalesRange', '年销售额', src.annualSalesRange)
        push('companyScale', '企业规模', src.companyScale)
        push('serviceArea', '服务区域', src.serviceArea)
        push('expertise', '擅长领域', src.expertise)
        return rows
      }
      if (k === 'supplier') {
        push('legalPerson', '企业负责人', src.legalPerson)
        push('registerCapital', '注册资本', src.registerCapital)
        push('operationScope', '经营范围', safeText(src.operationScope))
        push('address', '详细地址', src.address)
        return rows
      }
      push('companyName', '企业名称', src.companyName)
      push('contactName', '联系人', src.contactName)
      push('mobilePhone', '联系方式', src.mobilePhone)
      return rows
    },
    introText() {
      const src = this.profileSource || {}
      const k = this.receiveTypeKey
      if (k === 'scheme') return safeText(src.profile, '暂无简介')
      if (k === 'company') {
        const parts = [safeText(src.remark, ''), safeText(src.industry, '')].filter((x) => x && x !== '-')
        if (!parts.length) return '暂无简介'
        return `该企业「${safeText(src.companyName, '云企业')}」${parts.join('；')}。`.replace(/。+$/, '。')
      }
      if (k === 'supplier') return safeText(src.remark || src.operationScope, '暂无简介')
      return safeText(src.remark || src.profile, '暂无简介')
    },
    aiInsightText() {
      const t = this.status && this.status.aiCustomerInsight ? String(this.status.aiCustomerInsight).trim() : ''
      return t || '暂无分析结论（请稍后刷新或待对方完善认证信息后重试）。'
    },
    qualificationList() {
      const raw = this.status && this.status.qualificationHints
      if (!raw || !Array.isArray(raw)) return []
      return raw.map((x) => String(x || '').trim()).filter(Boolean)
    },
    schemeSolutionRows() {
      if (this.receiveTypeKey !== 'scheme') return []
      const raw = this.status && this.status.schemeSolutions
      if (!raw || !Array.isArray(raw)) return []
      return raw
    }
  },
  watch: {
    enabled: {
      handler(val) {
        if (val) this.refresh()
      },
      immediate: true
    },
    conversationId() {
      if (this.enabled) this.refresh()
    },
    targetUserId() {
      if (this.enabled) this.refresh()
    },
    entityId() {
      if (this.enabled) this.refresh()
    },
    viewStatus: {
      handler() {
        this.reconcilePolling()
      },
      immediate: true
    },
    requestId() {
      this.reconcilePolling()
    }
  },
  mounted() {
    this.reconcilePolling()
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange)
    }
  },
  beforeDestroy() {
    this.stopPolling()
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
    }
  },
  methods: {
    safeText,
    tipDisabled(val) {
      const s = val == null ? '' : String(val).trim()
      return !s || s === '-'
    },
    tooltipPlain(val) {
      if (this.tipDisabled(val)) return ''
      return String(val).trim()
    },
    gridValueEllipClass(cell) {
      if (!cell || !cell.key) return 'block-ellip-2'
      if (this.receiveTypeKey === 'supplier' && (cell.key === 'operationScope' || cell.key === 'address')) {
        return 'block-ellip-1'
      }
      return 'block-ellip-2'
    },
    buildParams() {
      return {
        bizType: this.chatKind || '',
        bizEntityId: this.entityId || '',
        targetUserId: this.targetUserId || '',
        conversationId: this.conversationId || '',
        receiveType: this.targetType || ''
      }
    },
    async refresh() {
      if (!this.enabled) return
      if (!this.targetUserId || !this.currentUserId) {
        this.status = {
          viewStatus: 'MASKED',
          maskedProfile: {
            companyName: '-',
            contactName: '-',
            mobilePhone: '-',
            industry: '-',
            region: '-',
            tags: [],
            summary: '-'
          }
        }
        this.emitProfileSync()
        return
      }

      this.loading = true
      try {
        const data = await fetchProfileViewStatus(this.buildParams(), { silent: true })
        this.status = data || {
          viewStatus: 'MASKED',
          maskedProfile: {}
        }
      } catch (e) {
        this.status = {
          viewStatus: 'MASKED',
          maskedProfile: {}
        }
      } finally {
        this.loading = false
        this.reconcilePolling()
        this.emitProfileSync()
      }
    },
    emitProfileSync() {
      this.$emit('profile-view-sync', {
        viewStatus: this.viewStatus,
        chatKind: this.chatKind || '',
        entityId: this.entityId || '',
        conversationId: this.conversationId || '',
        targetUserId: this.targetUserId || ''
      })
    },
    async handleRequest() {
      if (!this.canRequest) return
      this.actionLoading = true
      try {
        const res = await requestProfileView(this.buildParams(), { silent: true })
        if (res) {
          this.status = { ...(this.status || {}), ...(res || {}) }
        } else {
          await this.refresh()
        }
        this.$message && this.$message.success('已提交申请')
      } catch (e) {
        this.$message && this.$message.error('提交失败')
      } finally {
        this.actionLoading = false
      }
    },
    async handleApprove() {
      if (!this.canApproveOrReject) return
      this.actionLoading = true
      try {
        await approveProfileViewRequest(this.requestId, { silent: true })
        await this.refresh()
        this.$message && this.$message.success('已同意')
      } catch (e) {
        this.$message && this.$message.error('操作失败')
      } finally {
        this.actionLoading = false
      }
    },
    async handleReject() {
      if (!this.canApproveOrReject) return
      this.actionLoading = true
      try {
        await rejectProfileViewRequest(this.requestId, { silent: true })
        await this.refresh()
        this.$message && this.$message.success('已拒绝')
      } catch (e) {
        this.$message && this.$message.error('操作失败')
      } finally {
        this.actionLoading = false
      }
    },
    onVisibilityChange() {
      if (!this.enabled) return
      if (document.hidden) {
        this.stopPolling()
      } else {
        this.reconcilePolling(true)
      }
    },
    shouldPoll() {
      if (!this.enabled) return false
      const s = this.viewStatus
      const hasReq = !!this.requestId
      // 只在“流程中”自动刷新：待同意/待处理 且有 requestId
      if (!(s === 'PENDING' || s === 'NEED_ACTION')) return false
      if (!hasReq) return false
      // 仅在短时窗口内刷新，避免一直刷影响体验
      return Date.now() < Number(this.pollWindowUntil || 0)
    },
    reconcilePolling(forceRefresh = false) {
      if (typeof document !== 'undefined' && document.hidden) {
        this.stopPolling()
        return
      }
      const need = this.shouldPoll()
      if (!need) {
        this.stopPolling()
        return
      }
      this.startPolling()
      if (forceRefresh) this.refresh()
    },
    openPollWindow(ms = 60000) {
      this.pollWindowUntil = Date.now() + Math.max(0, Number(ms || 0))
      this.pollAttempts = 0
      this.reconcilePolling(true)
    },
    startPolling() {
      if (this.pollTimer) return
      const tick = async () => {
        this.pollTimer = null
        if (!this.shouldPoll()) {
          this.stopPolling()
          return
        }
        if (!this.loading && !this.actionLoading) {
          await this.refresh()
        }
        this.pollAttempts += 1
        // 退避：2s -> 4s -> 6s -> 8s -> 10s（上限 10s）
        const nextDelay = Math.min(10000, 2000 + this.pollAttempts * 2000)
        this.pollTimer = setTimeout(tick, nextDelay)
      }
      this.pollTimer = setTimeout(tick, 1200)
    },
    stopPolling() {
      if (!this.pollTimer) return
      clearTimeout(this.pollTimer)
      this.pollTimer = null
    }
  }
}

function firstNonDash(a, b) {
  const x = a != null && String(a).trim() ? String(a).trim() : ''
  if (x) return x
  const y = b != null && String(b).trim() ? String(b).trim() : ''
  return y
}
</script>

<style scoped>
.detail-wrap {
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.detail-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.detail-fill {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.block-ellip-1 {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.block-ellip-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  min-width: 0;
}

.block-ellip-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  min-width: 0;
}

.block-ellip-4 {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  min-width: 0;
}

.block-ellip-5 {
  display: -webkit-box;
  -webkit-line-clamp: 5;
  line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  min-width: 0;
}

.response-unified {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.response-unified-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px 10px;
  border-bottom: 1px solid #f0f0f0;
  background: linear-gradient(180deg, #ffffff, #fbfdff);
  flex-shrink: 0;
}

.response-main-title {
  font-size: 14px;
  font-weight: 900;
  color: #0f172a;
  letter-spacing: 0.02em;
}

.response-unified-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.response-scroll {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 8px 14px 10px;
  -webkit-overflow-scrolling: touch;
}

.response-entity-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 0 10px;
  min-width: 0;
  flex-shrink: 0;
}

.entity-name-tip {
  flex: 1;
  min-width: 0;
  display: block;
}

.company-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #1677ff, #096dd9);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 18px;
  font-weight: 800;
  flex-shrink: 0;
}

.company-name {
  font-size: 16px;
  font-weight: 800;
  color: #1a1a1a;
  line-height: 1.35;
  cursor: default;
}

.match-badge {
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
  flex-shrink: 0;
  border: 1px solid transparent;
}

.match-badge.ok {
  background: #f6ffed;
  border-color: #b7eb8f;
  color: #389e0d;
}

.match-badge.wait {
  background: #e6f4ff;
  border-color: #91caff;
  color: #1677ff;
}

.match-badge.warn {
  background: #fffbe6;
  border-color: #ffe58f;
  color: #d48806;
}

.match-badge.muted {
  background: #f5f5f5;
  border-color: #d9d9d9;
  color: #595959;
}

.info-bar {
  padding: 10px 14px;
  background: #f5f7fb;
  border-top: 1px solid #f3f4f6;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  font-size: 12px;
  flex-shrink: 0;
}

.info-bar--in-card {
  margin-left: -12px;
  margin-right: -12px;
  width: auto;
  max-width: none;
  box-sizing: border-box;
}

.info-item {
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
  max-width: 100%;
  flex: 1 1 calc(50% - 8px);
}

.info-label {
  color: #8c8c8c;
  font-weight: 600;
  flex-shrink: 0;
}

.info-value-tip {
  flex: 1;
  min-width: 0;
  display: block;
}

.cell-value-tip {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
}

.info-value {
  color: #333;
  font-weight: 600;
  min-width: 0;
  cursor: default;
}

.p-empty {
  padding: 14px 16px 16px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.7;
  flex-shrink: 0;
}

.p-loading {
  padding: 14px 16px;
  font-size: 12px;
  color: #64748b;
  flex-shrink: 0;
}

.p-info {
  border-radius: 12px;
  border: 1px solid #eef0f5;
  background: #fafafa;
  position: relative;
  overflow: hidden;
}

.p-info.blurred .blur-inner {
  filter: blur(7px);
  opacity: 0.9;
  user-select: none;
  pointer-events: none;
}

.p-info.blurred::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  pointer-events: none;
}

.p-blur-tip {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
  color: rgba(15, 23, 42, 0.85);
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.1);
  white-space: nowrap;
  pointer-events: none;
}

.blur-inner {
  padding: 12px;
}

.blur-inner .response-entity-row {
  padding-bottom: 10px;
  margin-bottom: 2px;
  border-bottom: 1px solid #eef0f5;
}

.section-title {
  font-size: 13px;
  font-weight: 800;
  color: #1677ff;
  margin: 10px 0 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding-left: 8px;
  border-left: 3px solid #1677ff;
}

.blur-inner > .section-title:first-of-type {
  margin-top: 4px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 4px;
}

.detail-item {
  background: #f8f9fc;
  border: 1px solid #eef0f5;
  border-radius: 10px;
  padding: 8px 10px;
  min-width: 0;
  overflow: hidden;
}

.item-label {
  font-size: 11px;
  color: #8c8c8c;
  margin-bottom: 4px;
}

.item-value {
  font-size: 12px;
  color: #333;
  font-weight: 600;
  line-height: 1.55;
  cursor: default;
  max-width: 100%;
}

.profile-section {
  background: #f9f9f9;
  border-radius: 10px;
  border-left: 4px solid #1677ff;
  padding: 10px 12px;
  margin-bottom: 4px;
}

.profile-text {
  font-size: 12px;
  line-height: 1.7;
  color: #555;
  cursor: default;
}

.resource-section {
  background: linear-gradient(135deg, #f0f5ff 0%, #e6f4ff 100%);
  border-radius: 10px;
  border: 1px solid #bae0ff;
  padding: 10px 12px;
  margin-bottom: 4px;
}

.resource-title {
  font-size: 12px;
  font-weight: 800;
  color: #1677ff;
  margin-bottom: 6px;
}

.resource-text {
  font-size: 12px;
  line-height: 1.75;
  color: #595959;
  cursor: default;
}

.attachment-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 4px;
}

.attach-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: #fafafa;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  font-size: 12px;
  min-width: 0;
}

.attach-name {
  color: #333;
  font-weight: 600;
  flex: 1;
  min-width: 0;
  cursor: default;
}

.scheme-solutions--in-card {
  margin-top: 8px;
  padding-top: 4px;
  border-top: 1px dashed #e5e7eb;
}

.scheme-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.scheme-li {
  border: 1px solid #eef0f5;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fbfdff;
  min-width: 0;
}

.scheme-name {
  font-size: 13px;
  font-weight: 900;
  color: #1a1a1a;
  line-height: 1.4;
  cursor: default;
}

.scheme-muted {
  font-size: 12px;
  font-weight: 600;
  color: #999;
}

.scheme-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #1677ff;
  font-weight: 700;
  cursor: default;
}

.scheme-desc {
  margin-top: 4px;
  font-size: 12px;
  color: #595959;
  line-height: 1.55;
  cursor: default;
}

.p-note {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #eef0f5;
  background: #f8f9fc;
  font-size: 12px;
  color: #475569;
  line-height: 1.7;
  cursor: default;
}

.p-actions {
  margin-top: 12px;
  padding: 0 14px 4px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.btn {
  padding: 9px 14px;
  border-radius: 10px;
  border: none;
  font-weight: 800;
  letter-spacing: 0.01em;
  font-size: 12px;
}

.btn-primary {
  background: linear-gradient(135deg, #1677ff 0%, #096dd9 100%);
  color: #fff;
  box-shadow: 0 8px 18px rgba(22, 119, 255, 0.28);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #d9d9d9;
  color: #334155;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
}
</style>

<style>
/* el-tooltip 气泡挂在 body，需非 scoped */
.profile-panel-tip {
  max-width: 340px;
  line-height: 1.55;
  word-break: break-word;
  white-space: normal;
}
</style>
